import type {TagCloseButtonProps} from '@chakra-ui/react';
import {Stack, TagCloseButton, TagLabel, chakra} from '@chakra-ui/react';
import {forwardRef} from '@chakra-ui/system';
import {faChevronDown} from '@fortawesome/pro-light-svg-icons/faChevronDown';
import {faTimes} from '@fortawesome/pro-light-svg-icons/faTimes';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {CreatableSelect, Select, chakraComponents, createFilter} from 'chakra-react-select';
import {useMemo} from 'react';
import type {ReactElement, RefAttributes} from 'react';

import {withStylesProvider} from '../../utils/withStylesProvider';
import {Tag} from '../Tag';

import {MenuList} from './components/MenuList';
import {StylesProvider, useStyles} from './theme/context';
import {selectTheme} from './theme/theme';

import type {
  GroupBase,
  IndicatorSeparatorProps,
  OptionBase,
  SelectComponentsConfig,
  Props,
  SelectInstance,
  CreatableProps,
} from 'chakra-react-select';

type CustomSelectComponent = <
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: Props<Option, IsMulti, Group> &
    RefAttributes<SelectInstance<Option, IsMulti, Group>> & {
      isCreatable?: boolean;
    } & Partial<
      Pick<
        CreatableProps<Option, IsMulti, Group>,
        'formatCreateLabel' | 'allowCreateWhileLoading' | 'onCreateOption' | 'isValidNewOption' | 'createOptionPosition'
      >
    >,
) => ReactElement<any>;

export interface Option extends OptionBase {
  value: string;
  label: string;
}

const Icon = chakra(FontAwesomeIcon, {
  baseStyle: {
    width: `1em`,
    height: `1em`,
    fontSize: `md`,
  },
});

export const IndicatorSeparator = (_props: IndicatorSeparatorProps<any, any, any>) => null;

export const components: SelectComponentsConfig<Option, false, GroupBase<Option>> = {
  SelectContainer: props => {
    const dataAttributes = useMemo(
      () =>
        Object.entries(props.selectProps ?? {})
          .filter(([key]) => key.startsWith(`data-`))
          .reduce((acc, [key, value]) => ({...acc, [key]: value}), {}),
      [props.selectProps],
    );
    return <chakraComponents.SelectContainer {...props} innerProps={{...dataAttributes, ...props.innerProps}} />;
  },
  DropdownIndicator: ({children: _c, ...props}) => {
    if (props.selectProps.isLoading) {
      return null;
    }
    return (
      <chakraComponents.DropdownIndicator {...props}>
        <Icon fontSize={`md`} icon={faChevronDown} transform={props.isFocused ? `rotate(180deg)` : undefined} />
      </chakraComponents.DropdownIndicator>
    );
  },
  ClearIndicator: ({children: _c, ...props}) => {
    return (
      <chakraComponents.ClearIndicator {...props}>
        <Icon fontSize={`md`} icon={faTimes} />
      </chakraComponents.ClearIndicator>
    );
  },
  LoadingIndicator: props => {
    return <chakraComponents.LoadingIndicator spinnerSize={`sm`} thickness={`1px`} {...props} />;
  },
  MultiValue: ({innerProps, children, removeProps}) => {
    return (
      <Tag variant={`secondary`} size={`sm`} {...innerProps}>
        <TagLabel>{children}</TagLabel>
        <TagCloseButton {...(removeProps as TagCloseButtonProps)} />
      </Tag>
    );
  },
  MultiValueContainer: ({innerProps, children}) => {
    return (
      <Stack {...innerProps} spacing={1} direction={`row`}>
        {children}
      </Stack>
    );
  },
};

const InternalCustomSelect: CustomSelectComponent = forwardRef((props, ref) => {
  const {control, option} = useStyles();
  const isCreatable = props.isCreatable ?? false;
  const Component = useMemo(() => (isCreatable ? CreatableSelect : Select), [isCreatable]);
  return (
    <Component
      {...props}
      ref={ref}
      filterOption={props.filterOption ?? createFilter({ignoreAccents: false})}
      chakraStyles={{
        ...props.chakraStyles,
        control: (provided, state) => {
          return {
            ...provided,
            ...control,
            ...props.chakraStyles?.control?.(provided, state),
          };
        },
        option: (provided, state) => {
          return {
            ...provided,
            ...option,
            ...props.chakraStyles?.option?.(provided, state),
          };
        },
      }}
      // @ts-expect-error chakra-react-select types not matching but functioning correctly
      components={{
        ...components,
        MenuList,
        IndicatorSeparator,
        ...props.components,
      }}
    />
  );
});

export const CustomSelect = withStylesProvider(
  InternalCustomSelect,
  StylesProvider,
  selectTheme,
) as typeof InternalCustomSelect;
